import {Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {translatedMetalLevels} from '../../../../helpers/catalogMessages'
import {TFormAllValues} from '../../../policies/policiesTypes'
import {useLazyQuery, useQuery} from '@apollo/client'
import {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {IResPoliciesType} from '../../../../types/policies'
import {GET_ALL_POLICIES_TYPES} from '../../../../gql/queries/policiesQueries'
import {InputCurrency} from 'app/tfi/components/InputCurrency'
import {FormikErrors, FormikTouched} from 'formik'
import {IResCarriersList} from '../../../../types/carriers'
import {useDisclourse} from '../../../../hooks/useDisclourse'
import {IResultPlanJson, Plan} from '../../../oneLead/oneleadTypes'
import {ModalInfoPlan} from '../../../oneAddQuote/components/ModalInfoPlan'
import {GET_MARKET_PLACE_2} from '../../../../gql/queries/leadsQuery'
import {IResTest} from '../../../../types/leads'
import {normalizeAmount} from '../../../../helpers/consts'
import {mapFormQuoteToQuoteData} from '../../utils-sold'
import {IResStates} from '../../../../types/statesEU'
import {DateInput} from 'app/tfi/components/DateInput'
import {getYearFromEffectiveDate, parseId} from '../../../../helpers'
import {
  CARRIER_CATEGORIES_BY_KIND,
  isAcaKind,
  isLifeKind,
  TPolicyFormKind,
} from '../../../../helpers/policyFormKind'
import {LifeProposalSection} from './LifeProposalSection'

interface PolicyInsuranceProps {
  /**
   * Resuelto por el backend (`policy_type.canEditEffectiveDate`). En ACA ya completada la
   * vigencia la manda el carrier: cambiarla a mano descuadra comisiones y cohortes, asi que
   * solo la edita quien tenga el permiso `change_policy_effective_date`. La regla NO se
   * reimplementa aca — el front solo la pinta.
   */
  canEditEffectiveDate?: boolean
  onPrevStep: () => void
  onNextStep: () => void
  handleChange: (field: string, value: string) => void
  handleBlur: (field: string) => void
  allValues: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  loadingCreate: boolean
  loadingCarriers: boolean
  dataCarriers: IResCarriersList | undefined
  dataStates: IResStates | undefined
  isEdit?: boolean
  formKind?: TPolicyFormKind
}

export const PolicyInsuranceTab: React.FC<PolicyInsuranceProps> = ({
  canEditEffectiveDate = true,
  onPrevStep,
  onNextStep,
  handleBlur,
  handleChange,
  allValues,
  errors,
  touched,
  loadingCreate,
  loadingCarriers,
  dataCarriers,
  dataStates,
  isEdit = false,
  formKind = 'GENERIC',
}) => {
  const intl = useIntl()
  const isACA = isAcaKind(formKind)
  const isLife = isLifeKind(formKind)
  // El dropdown de carriers solo se acota en Vida, donde la lista de aseguradoras es
  // muy distinta a la de salud. En el resto de los tipos se muestran todas y ni
  // siquiera aparece el control. En Vida arranca filtrado y el usuario decide.
  const [showAllCarriers, setShowAllCarriers] = useState(!isLife)

  // Al cambiar el tipo de póliza se vuelve a aplicar el default del nuevo tipo.
  useEffect(() => {
    setShowAllCarriers(!isLifeKind(formKind))
  }, [formKind])
  const selectOnePlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })
  const [infoPlan, setInfoPlan] = useState<Plan>()
  const {isOpen, onClose, onOpen} = useDisclourse()

  const [gqlGetMarket, {data: dataMarket}] =
    useLazyQuery<IResTest>(GET_MARKET_PLACE_2, {
      fetchPolicy: 'no-cache',
    })

  const handleSearchPlan = () => {
    const vars = mapFormQuoteToQuoteData(allValues, dataStates)
    vars.queryType = 'BUSCAR_PLAN_ID'
    vars.planIds = [allValues.CMSPlanId]

    const {countyfipsName, limit, offset, utilizationLevel, order, ...cleaned} = vars

    const arrPe = [
      {
        ...vars?.personOne,
        age: Number(vars.personOne.age),
        pregnantWith: vars.personOne.pregnantWith ? Number(vars.personOne.pregnantWith) : 0,
        currentEnrollment: vars.personOne.currentEnrollment
          ? {
              ...vars.personOne.currentEnrollment,
              id: undefined,
            }
          : null,
      },
      ...((vars.people ?? []).map((inf) => ({
        ...inf,
        id: undefined,
        age: Number(inf.age),
        pregnantWith: inf.pregnantWith ? Number(inf.pregnantWith) : 0,
        currentEnrollment: inf.currentEnrollment
          ? {
              effectiveDate: inf.currentEnrollment.effectiveDate,
              planId: inf.currentEnrollment.planId,
              useTobacco: inf.currentEnrollment.useTobacco,
            }
          : null,
      })) || []),
    ]

    console.log('handleSearchPlan', {
      ...cleaned,
      householdIncome: Number(cleaned.householdIncome.replaceAll(',', '')),
      personOne: undefined,
      people: arrPe,
    })
    gqlGetMarket({
      variables: {
        ...cleaned,
        year: getYearFromEffectiveDate(cleaned.effectiveDate),
        householdIncome: Number(cleaned.householdIncome.replaceAll(',', '')),
        personOne: undefined,
        people: arrPe,
      },
    })
    onOpen()
  }

  useEffect(() => {
    if (!dataMarket) return

    if (dataMarket.marketplace2) {
      const json = JSON.parse(dataMarket.marketplace2) as IResultPlanJson

      if (json.error) {
        toast.error(
          intl.formatMessage({
            id: 'policies.detail.form.insurance.toast.quoteNotFound',
            defaultMessage: 'Error: Quote not found',
          })
        )
        return
      }

      if (json.validation_errors) {
        toast.error(
          intl.formatMessage(
            {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
            {message: json.validation_errors[0].error}
          )
        )
        return
      }
      // console.log('json.plans', json)
      setInfoPlan(json.plan)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMarket])

  const {data, error} = useQuery<IResPoliciesType>(GET_ALL_POLICIES_TYPES, {
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (error) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const safeParseId = (value: string) => {
    try {
      return String(parseId(value))
    } catch {
      return value
    }
  }

  const selectedCarrierValue = useMemo(() => {
    if (!dataCarriers || !allValues.carrier) return allValues.carrier

    const current = String(allValues.carrier)
    const exactMatch = dataCarriers.allInsuranceOperatorsList.find(
      (carrier) => String(carrier.id) === current
    )
    if (exactMatch) return exactMatch.id

    const normalizedMatch = dataCarriers.allInsuranceOperatorsList.find(
      (carrier) => safeParseId(String(carrier.id)) === safeParseId(current)
    )
    return normalizedMatch?.id ?? allValues.carrier
  }, [dataCarriers, allValues.carrier])

  /**
   * El dropdown de carriers se acota por `Insurance_category` según el tipo de póliza
   * (Vida → LIFE INSURANCE / FINAL EXPENSE / ANNUITIES). El carrier ya guardado siempre
   * se mantiene en la lista, aunque su categoría no coincida, para no perderlo al editar.
   */
  const carrierOptions = useMemo(() => {
    const all = dataCarriers?.allInsuranceOperatorsList ?? []
    const categories = CARRIER_CATEGORIES_BY_KIND[formKind]
    // Fuera de Vida nunca se filtra, aunque el kind tenga categorías configuradas.
    if (!isLife || showAllCarriers || !categories?.length) return all

    const selected = String(selectedCarrierValue ?? '')
    const filtered = all.filter((carrier) => {
      if (selected && String(carrier.id) === selected) return true
      const name = (carrier.category?.name || '').toUpperCase()
      return categories.includes(name)
    })
    return filtered.length ? filtered : all
  }, [dataCarriers, formKind, isLife, showAllCarriers, selectedCarrierValue])

  // Solo Vida ofrece el filtro; el resto de los tipos ve la lista completa.
  const canFilterCarriers = isLife && !!CARRIER_CATEGORIES_BY_KIND[formKind]?.length

  return (
    <>
      <div className='policy-insurance-members-container'>
        <div className='kt-heading kt-heading--md custom-border-brand'>
          <FormattedMessage
            id='policies.detail.form.insurance.heading'
            defaultMessage='Policy insurance information'
          />
        </div>
        <Row>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.basic.policyType'
                    defaultMessage='Policy type'
                  /> <small className='text-danger'>
                    <FormattedMessage id='common.hint.required' defaultMessage='(required)' />
                  </small>
                </Form.Label>
                <Form.Select
                  onChange={(e) => handleChange('typePolicy', '' + e.target.value)}
                  value={allValues.typePolicy}
                  onBlur={() => handleBlur('typePolicy')}
                  isInvalid={Boolean(errors.typePolicy && touched.typePolicy)}
                >
                  <option value=''>{selectOnePlaceholder}</option>
                  {data &&
                    data.allPoliciesType.edges.map(({node}) => (
                      <option key={node.id} value={node.id}>
                        {node.typePolicy}
                      </option>
                    ))}
                </Form.Select>
                {errors.typePolicy && touched.typePolicy && (
                  <></>
                  // <Form.Control.Feedback type='invalid'>{errors.typePolicy}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
          {/* Bloque exclusivo de Marketplace: fuera de ACA nada de esto aplica. */}
          {isACA && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='policies.detail.form.insurance.ffm' defaultMessage='FFM' />
                  </Form.Label>
                  <Form.Control
                    placeholder={intl.formatMessage({
                      id: 'policies.detail.form.insurance.ffmPlaceholder',
                      defaultMessage: 'FFM used in marketplace',
                    })}
                    onChange={(e) => handleChange('FFM', e.target.value)}
                    value={allValues.FFM}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='policies.detail.form.insurance.npn' defaultMessage='NPN' />
                  </Form.Label>
                  <Form.Control
                    placeholder={intl.formatMessage({
                      id: 'policies.detail.form.insurance.npnPlaceholder',
                      defaultMessage: 'NPN used in marketplace',
                    })}
                    onChange={(e) => handleChange('NPN', e.target.value)}
                    value={allValues.NPN}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='policies.detail.form.insurance.typeSale'
                      defaultMessage='Type of Sale'
                    />
                  </Form.Label>
                  <Form.Select
                    onChange={(e) => handleChange('typeSale', e.target.value)}
                    value={allValues.typeSale}
                  >
                    <option value=''>{selectOnePlaceholder}</option>
                    <option value='New sale'>
                      {intl.formatMessage({
                        id: 'policies.list.wizard.quote.newSale',
                        defaultMessage: 'New sale',
                      })}
                    </option>
                    <option value='Renewal'>
                      {intl.formatMessage({
                        id: 'policies.list.wizard.quote.renewal',
                        defaultMessage: 'Renewal',
                      })}
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </>
          )}

          <Col md={4}>
            <Form.Group className='mt-2'>
              <DateInput
                id="effectiveDate"
                name="effectiveDate"
                label={intl.formatMessage({
                  id: 'policies.list.wizard.basic.effectiveDate',
                  defaultMessage: 'Effective date',
                })}
                value={allValues.effectiveDate}
                error={errors.effectiveDate}
                touched={touched.effectiveDate}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!canEditEffectiveDate}
              />
              {!canEditEffectiveDate && (
                <Form.Text className='text-muted'>
                  <i className='bi bi-lock-fill me-1' />
                  <FormattedMessage
                    id='policies.detail.form.insurance.effectiveDateLocked'
                    defaultMessage='Locked: the effective date of a completed ACA policy is managed by Operations.'
                  />
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          {/* Member ID aplica a cualquier tipo de póliza: la aseguradora asigna un
              número de miembro tanto en salud como en vida. */}
          <Col md={4}>
            <Form.Group className='mb-3'>
              <Form.Label>
                <FormattedMessage
                  id='policies.list.preview.field.memberId'
                  defaultMessage='Member ID'
                />
              </Form.Label>
              <Form.Control
                value={allValues.memberId}
                onChange={(e) => handleChange('memberId', e.target.value)}
                name='memberId'
                id='memberId'
                placeholder={intl.formatMessage({
                  id: 'policies.list.quickUpdate.phMemberId',
                  defaultMessage: 'Enter Member ID',
                })}
              />
            </Form.Group>
          </Col>

          {isACA && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='policies.list.wizard.quote.marketplaceId'
                      defaultMessage='Marketplace ID'
                    />
                  </Form.Label>
                  <Form.Control
                    placeholder={intl.formatMessage({
                      id: 'policies.detail.form.insurance.marketplaceIdPlaceholder',
                      defaultMessage: 'Marketplace number',
                    })}
                    onChange={(e) => handleChange('marketplaceId', e.target.value)}
                    value={allValues.marketplaceId}
                  />
                </Form.Group>
              </Col>

              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage
                        id='policies.list.wizard.quote.cmsPlan'
                        defaultMessage='CMS Plan'
                      />
                    </Form.Label>
                    <InputGroup className='mb-3'>
                      <Form.Control
                        placeholder={intl.formatMessage({
                          id: 'policies.detail.form.insurance.cmsPlanPlaceholder',
                          defaultMessage: 'CMS Plan id',
                        })}
                        id='planid'
                        name='planid'
                        value={allValues.CMSPlanId}
                        onChange={(e) => handleChange('CMSPlanId', e.target.value)}
                      />
                      <Button
                        variant='outline-secondary'
                        onClick={handleSearchPlan}
                        className='search-cms-plan'
                        id='button-search-cms-plan'
                      >
                        <i className='bi bi-search' />
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          <Col md={4} className='mb-5'>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.list.filters.field.carrier'
                  defaultMessage='Carrier'
                />
              </Form.Label> {loadingCarriers && <Spinner animation='border' size='sm' />}
              {dataCarriers ? (
                <>
                  <Form.Select
                    onChange={(e) => handleChange('carrier', '' + e.target.value)}
                    value={selectedCarrierValue || allValues.carrier}
                    onBlur={() => handleBlur('carrier')}
                  >
                    <option value=''>{selectOnePlaceholder}</option>
                    {carrierOptions.map((carrier) => (
                      <option key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </option>
                    ))}
                  </Form.Select>
                  {canFilterCarriers && (
                    <Form.Check
                      type='checkbox'
                      className='mt-1'
                      id='show-all-carriers'
                      checked={showAllCarriers}
                      onChange={(e) => setShowAllCarriers(e.target.checked)}
                      label={
                        <small className='text-muted'>
                          <FormattedMessage
                            id='policies.detail.form.insurance.showAllCarriers'
                            defaultMessage='Show all carriers'
                          />
                        </small>
                      }
                    />
                  )}
                </>
              ) : (
                <Form.Control readOnly value={allValues.carrier ?? ''} />
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.list.wizard.quote.planName'
                  defaultMessage='Plan Name'
                />
              </Form.Label>
              <Form.Control
                onChange={(e) => handleChange('planName', e.target.value)}
                value={allValues.planName}
              />
            </Form.Group>
          </Col>
          {isACA && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='policies.list.filters.field.metalLevel'
                      defaultMessage='Metal Level'
                    /> <small className='text-danger'>
                      <FormattedMessage id='common.hint.required' defaultMessage='(required)' />
                    </small>
                  </Form.Label>
                  <Form.Select
                    onChange={(e) => handleChange('metalLevel', e.target.value)}
                    value={allValues.metalLevel}
                    onBlur={() => handleBlur('metalLevel')}
                    isInvalid={Boolean(errors.metalLevel && touched.metalLevel)}
                  >
                    <option value=''>{selectOnePlaceholder}</option>
                    {translatedMetalLevels(intl).map(({value, label}) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.metalLevel && touched.metalLevel && (
                    <Form.Control.Feedback type='invalid'>{errors.metalLevel}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='policies.list.wizard.quote.policyTotalCost'
                      defaultMessage='Policy Total Cost'
                    />
                  </Form.Label>
                  <InputCurrency
                    value={normalizeAmount(allValues.policyTotalCost)}
                    onValueChange={(v) => handleChange('policyTotalCost', v ?? '')}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='policies.detail.form.insurance.taxCredit'
                      defaultMessage='Tax Credit / Subsidy'
                    />
                  </Form.Label>
                  <InputCurrency
                    value={normalizeAmount(allValues.taxCredit)}
                    onValueChange={(v) => handleChange('taxCredit', v ?? '')}
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col md={4} className='mb-4'>
            <Form.Group>
              <Form.Label>
                {isLife ? (
                  <FormattedMessage
                    id='policies.detail.form.life.proposedPremium'
                    defaultMessage='Proposed premium'
                  />
                ) : (
                  <FormattedMessage
                    id='policies.detail.form.insurance.monthlyPremium'
                    defaultMessage='Monthly Premium'
                  />
                )}
              </Form.Label>
              <InputCurrency
                value={normalizeAmount(allValues.premium)}
                onValueChange={(v) => handleChange('premium', v ?? '')}
              />
            </Form.Group>
          </Col>
        </Row>

        {isLife && (
          <LifeProposalSection
            values={allValues.policyLifeDetail}
            errors={errors}
            touched={touched}
            handleChange={(field, value) => handleChange(`policyLifeDetail.${field}`, value)}
            handleBlur={(field) => handleBlur(`policyLifeDetail.${field}`)}
            holder={allValues.holder}
          />
        )}

        <div className='btn-prev-next'>
          <Button size='sm' variant='secondary' onClick={onPrevStep}>
            <FormattedMessage id='policies.list.wizard.previous' defaultMessage='PREVIOUS' />
          </Button>
          <Button size='sm' onClick={onNextStep}>
            <FormattedMessage id='policies.list.wizard.nextStep' defaultMessage='NEXT STEP' />
          </Button>
        </div>
        {/* AQUI PODRIA SER QUE MANDO EL PLAN ID en vez del INFO PLAN y adentro consulto todo el info delplan */}
        {infoPlan && isOpen && (
          <ModalInfoPlan isOpen={isOpen} onClose={onClose} planInput={infoPlan} isPreview={true} />
        )}
      </div>
    </>
  )
}
